import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, TableShell, EmptyState, Modal, Field, Input, SelectBox, Button, StatusBadge,
} from "@/components/ui-kit";
import { IconBtn } from "@/pages/Clients";
import { Plus, DollarSign, Pencil, Trash2 } from "lucide-react";

const EMPTY = { employee_id: "", period_start: "", period_end: "", hours: 0, jobs_completed: 0, gross_pay: 0, deductions: 0, status: "Pending", notes: "" };

export default function Payroll() {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const { t } = useI18n();

  const load = () => api.get("/payroll").then(({ data }) => setRecords(data));
  useEffect(() => { load(); api.get("/employees").then(({ data }) => setEmployees(data)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (r) => { setForm(r); setEditing(r.id); setOpen(true); };

  const save = async () => {
    if (!form.employee_id) return toast.error(t("Select employee"));
    const payload = { ...form, hours: Number(form.hours), jobs_completed: Number(form.jobs_completed), gross_pay: Number(form.gross_pay), deductions: Number(form.deductions) };
    try {
      if (editing) await api.put(`/payroll/${editing}`, payload);
      else await api.post("/payroll", payload);
      toast.success(editing ? t("Payroll updated") : t("Payroll record added"));
      setOpen(false); load();
    } catch { toast.error("Could not save payroll"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this payroll record?")) return;
    await api.delete(`/payroll/${id}`); toast.success(t("Deleted")); load();
  };

  const net = Number(form.gross_pay || 0) - Number(form.deductions || 0);

  return (
    <div data-testid="payroll-page">
      <PageHeader title="Payroll" subtitle="Run payroll for your crew by pay period."
        action={<PrimaryButton onClick={openNew} data-testid="add-payroll-button"><Plus size={16} /> {t("New Payroll")}</PrimaryButton>} />

      {records.length === 0 ? (
        <EmptyState icon={DollarSign} title="No payroll records" subtitle="Create a payroll record for an employee and pay period."
          action={<PrimaryButton onClick={openNew}><Plus size={16} /> {t("New Payroll")}</PrimaryButton>} />
      ) : (
        <TableShell headers={["Employee", "Period", "Hours", "Jobs", "Gross", "Net Pay", "Status", "Actions"]} testid="payroll-table">
          {records.map((r) => (
            <tr key={r.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd]">
              <td className="py-3.5 px-4 font-medium text-[#121212]">{r.employee_name}</td>
              <td className="py-3.5 px-4 text-sm text-[#52525B]">{r.period_start} → {r.period_end}</td>
              <td className="py-3.5 px-4 text-sm">{r.hours}</td>
              <td className="py-3.5 px-4 text-sm">{r.jobs_completed}</td>
              <td className="py-3.5 px-4 text-sm">{money(r.gross_pay)}</td>
              <td className="py-3.5 px-4 font-medium text-green-600">{money(r.net_pay)}</td>
              <td className="py-3.5 px-4"><StatusBadge status={r.status} /></td>
              <td className="py-3.5 px-4"><div className="flex gap-1"><IconBtn onClick={() => openEdit(r)} title="Edit"><Pencil size={16} /></IconBtn><IconBtn onClick={() => remove(r.id)} title="Delete" danger><Trash2 size={16} /></IconBtn></div></td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Payroll" : "New Payroll"} size="lg"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-payroll-button">{t("Save")}</PrimaryButton></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Employee *"><SelectBox value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} options={employees.map((emp) => ({ value: emp.id, label: emp.name }))} placeholder="Select employee" /></Field>
          <Field label="Status"><SelectBox value={form.status} onChange={(e) => set("status", e.target.value)} options={["Pending", "Paid"]} /></Field>
          <Field label="Period Start"><Input type="date" value={form.period_start} onChange={(e) => set("period_start", e.target.value)} /></Field>
          <Field label="Period End"><Input type="date" value={form.period_end} onChange={(e) => set("period_end", e.target.value)} /></Field>
          <Field label="Hours"><Input type="number" value={form.hours} onChange={(e) => set("hours", e.target.value)} /></Field>
          <Field label="Jobs Completed"><Input type="number" value={form.jobs_completed} onChange={(e) => set("jobs_completed", e.target.value)} /></Field>
          <Field label="Gross Pay ($)"><Input type="number" value={form.gross_pay} onChange={(e) => set("gross_pay", e.target.value)} /></Field>
          <Field label="Deductions ($)"><Input type="number" value={form.deductions} onChange={(e) => set("deductions", e.target.value)} /></Field>
        </div>
        <div className="mt-4 flex justify-end"><div className="bg-[#faf8fd] rounded-xl px-4 py-2 text-sm font-bold text-[#5A189A]">{t("Net Pay")}: {money(net)}</div></div>
      </Modal>
    </div>
  );
}
